/**
 * @file: useFacadeMediaUpload.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { applyOrderFacadeItemResult } from '@euler/app/flows/order/functions/applyOrderFacadeItemResult';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import {
  AbnormalLevel,
  OptionValueType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import { usePersistFn } from '@euler/utils/hooks';
import { nanoid } from 'nanoid/non-secure';
import { useEffect } from 'react';

export const useFacadeMediaUpload = () => {
  const {
    fileUploader,
    inventoryQuery,
    updateDetail,
    formController: { form },
  } = useOrderFlowContext();

  const setupFileUploaderEventListener = usePersistFn(() => {
    return fileUploader.addListener((e, task) => {
      if (task.request.context?.type !== 'facade-image') {
        return;
      }

      const siteId = task.request.context.siteId;
      const site = inventoryQuery.getSiteById(siteId);
      const checkItem = site?.checkItems[0];
      if (!site || !checkItem) return;

      const context = task.request.context;

      if (e.type === 'queued') {
        updateDetail(draft => {
          if (!draft.preInspection) draft.preInspection = [];
          let siteInspection = draft.preInspection.find(
            x => x.siteId === siteId,
          );
          if (siteInspection == null) {
            // create site inspection staged object and the item
            siteInspection = {
              flowId: '',
              technicianId: 0,
              technicianName: '',
              siteId,
              siteName: site.name,
              siteCode: site.code,
              abnormalLevel: AbnormalLevel.Fine,
              severityLevel: SeverityLevel.None,
              inspectionType: SiteInspectionType.Facade,
              items: [
                {
                  _key: nanoid(),
                  type: 'item',
                  itemId: checkItem.id,
                  name: checkItem.name,
                  abnormalLevel: AbnormalLevel.Fine,
                  severityLevel: SeverityLevel.None,
                  isCustom: false,
                  result: { valueType: OptionValueType.String, value: '' },
                  medias: [],
                },
              ],
            };
            draft.preInspection.push(siteInspection);
          }

          const item = siteInspection.items[0];
          if (!item) {
            console.error('siteInspection.items is empty');
            return;
          }

          if (context.mediaKey) {
            const index = item.medias.findIndex(
              x => x._key === context.mediaKey,
            );
            if (index >= 0) {
              const media = item.medias[index];
              if (context.annotationMetadata != null) {
                media.annotationMetadata = context.annotationMetadata as any;
              }
              media._source = {
                type: 'local',
                localFileUri: task.request.localFileUri,
                contentType: task.request.contentType,
                uploadParameters: task.request.parameters,
                state: { status: 'queued' },
              };
              applyOrderFacadeItemResult(item, site);
              return;
            }
          }

          item.medias.push({
            type: task.request.contentType,
            annotationMetadata: context.annotationMetadata as any,
            _key: context.mediaKey ?? task.key,
            _timestamp: Date.now(),
            _source: {
              type: 'local',
              localFileUri: task.request.localFileUri,
              contentType: task.request.contentType,
              uploadParameters: task.request.parameters,
              state: { status: 'queued' },
            },
          });

          applyOrderFacadeItemResult(item, site);
        });
      } else {
        updateDetail(draft => {
          const siteInspection = draft.preInspection?.find(
            x => x.siteId === siteId,
          );
          if (!siteInspection) {
            console.error(
              'site inspection is not initialized, this must be a bug',
            );
            return;
          }
          const media = siteInspection.items[0]?.medias.find(
            x => x._key === (context.mediaKey ?? task.key),
          );
          if (!media) {
            console.error(
              'cannot find the staged media object, this must be bug',
            );
            return;
          }
          if (media._source.type !== 'local') return;
          switch (e.type) {
            case 'compressing': {
              media._source.state = {
                status: 'compressing',
                progress: e.progress,
              };
              return;
            }
            case 'begin': {
              media._source.state = { status: 'uploading', progress: 0 };
              return;
            }
            case 'progress': {
              media._source.state = {
                status: 'uploading',
                progress: e.progress,
              };
              return;
            }
            case 'error': {
              media._source.state = { status: 'error', error: e.error };
              return;
            }
            case 'abort': {
              if (task.status !== 'aborted') {
                media._source.state = {
                  status: 'error',
                  error: e.error ?? new Error('File uploading has aborted'),
                };
              }
              return;
            }
            case 'finished': {
              const cover = e.result.extra?.cover;
              media.coverUrl =
                cover != null && cover !== '' ? cover : undefined;
              media._source = {
                type: 'remote',
                url: e.result.url,
                cid: e.result.name,
              };
              return;
            }
            default:
              return;
          }
        });
        if (e.type === 'finished') {
          form.facadeInspectionResults.validate();
        }
      }
    });
  });

  useEffect(() => {
    return setupFileUploaderEventListener();
  }, [setupFileUploaderEventListener]);
};
